import {
  ZERO, add, clampPos, formatMoney, kop, perUnitFloor, sub, type Money,
} from './money.js';
import {
  addDays, dayWords, daysInMonth, diffDays, isAfter, isBefore, nextOccurrence, nextOccurrenceInclusive,
  prevOccurrenceInclusive, ymd, type PlainDate,
} from './date.js';
import { creditState, type CreditState } from './credit.js';
import { cardPayments, notCovered } from './forecast.js';
import {
  confirmedDate, cycleStartFrom, nextForStream, resolveHorizon,
  type Horizon, type IncomeFact,
} from './income.js';
import {
  backedReserves, paymentInstances, unsettledInstances, type PaymentInstance,
} from './instances.js';
import { isFunded } from './savings.js';
import type {
  Account, CreditTerms, Goal, IncomeExpectation, IncomeStream, Loan, Obligation,
  PlannedExpense, Reserve, ReserveTarget,
} from './types.js';

export interface AvailableInput {
  today: PlainDate;
  accounts: Account[];
  creditTerms: CreditTerms[];
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  goals: Goal[];
  plannedExpenses: PlannedExpense[];
  /** Конверты под требования. Необязательны: без них расчёт работает как раньше. */
  reserves?: Reserve[];
  /**
   * Долги. Нужны, чтобы цель «закрыть долг» не вычла те же деньги второй раз:
   * под платёж по карте уже держится резерв беспроцентного периода.
   */
  loans?: Loan[];
  /**
   * Ожидаемые поступления. В сумму не входят **никогда** — участвуют только в
   * выборе горизонта, и только те, дату которых назвал человек.
   */
  expectations?: IncomeExpectation[];
  /** Приходы, которые случились: по ним видно подтверждение и ритм. */
  incomeFacts?: IncomeFact[];
  /** С какого дня приложение ведёт учёт. */
  trackingStartedAt?: PlainDate;
}

export type DeductionKind = 'reserve' | 'obligation' | 'credit' | 'goal' | 'planned';

export interface DeductionLine {
  kind: DeductionKind;
  /** Идентификатор требования: обязательства, карты, цели, брони. */
  id: string;
  /**
   * Ключ конкретного наступления платежа.
   *
   * У обязательства с недельным расписанием за цикл несколько наступлений,
   * и они различаются только этим ключом.
   */
  instanceId?: string;
  label: string;
  amount: Money;
  /** Поясняющая приписка — она обязательна там, где сумма иначе читается неверно. */
  note?: string;
  date?: PlainDate;
}

/**
 * Платёж, который наступает в этом цикле.
 *
 * Отдельно от вычетов, потому что это разные вопросы. Вычеты — арифметика:
 * платёж, полностью покрытый конвертом, из них уходит, иначе деньги вычтутся
 * дважды. Но пользователю он всё равно нужен на экране: исчезнувший платёж
 * читается как потерянный, а не как обеспеченный.
 */
export interface UpcomingPayment {
  kind: 'obligation' | 'credit';
  id: string;
  label: string;
  date: PlainDate;
  /** Полная сумма платежа. */
  amount: Money;
  /** Сколько уже закреплено конвертами или внесено. */
  covered: Money;
  /** Сколько ещё не обеспечено. */
  remaining: Money;
  /**
   * Деньги под платёж собраны полностью.
   *
   * Отдельно от `settled`: конверт обеспечивает платёж, но не выполняет его.
   * «Деньги готовы» и «оплачено» — разные сообщения, и для человека,
   * которому ещё предстоит нажать кнопку в банке, разница решающая.
   */
  secured: boolean;
  /** **Фактически оплачен**: подтверждён человеком либо внесён на карту. */
  settled: boolean;
  /** Срок наступил, а платёж не выполнен. Обеспеченность на это не влияет. */
  due: boolean;
  /**
   * Платёж наступает уже после ближайшего поступления.
   *
   * Из «Доступно» он не вычитается: за него отвечает будущий доход, а не
   * сегодняшний остаток. Но с экрана не убирается — исчезнувший платёж
   * читается как потерянный, а человеку важно знать, что он на горизонте.
   */
  afterHorizon: boolean;
}

export interface AvailableResult {
  /** Сумма ликвидных счетов — от неё считаем. */
  liquid: Money;
  liquidLines: DeductionLine[];
  deductions: DeductionLine[];
  groups: Record<DeductionKind, Money>;
  /** Сколько резервов лежит на неликвидных счетах — они уже вне доступного. */
  reservedOutsideLiquid: Money;
  /** Итог: сколько можно потратить до следующего поступления. */
  available: Money;
  /**
   * Конец окна расчёта. При неизвестном горизонте это конец месяца — окно
   * нужно арифметике, но датой прихода оно не является. Что именно известно,
   * говорит `horizon.basis`, и интерфейс обязан смотреть туда.
   */
  horizonDate: PlainDate;
  /** Что известно о следующем приходе: подтверждено, оценено или ничего. */
  horizon: Horizon;
  daysToHorizon: number;
  /** Темп: сколько в день. Округлён вниз до рубля — обещать лишнего нельзя. */
  perDay: Money;
  credit: CreditState[];
  /** Все платежи цикла: и обеспеченные, и нет, и уже оплаченные. */
  upcoming: UpcomingPayment[];
}

/**
 * Осторожная оценка дохода переехала в `income.ts`: её берёт прогноз по дням,
 * а он нужен и этому модулю. Имя остаётся доступным отсюда — на него
 * ссылаются расчёты и тесты.
 */
export { conservativeIncome } from './income.js';

/**
 * Горизонт — дата ближайшего поступления. Не «конец месяца».
 *
 * Считается по ритму источника (`income.ts`), а не по одному числу месяца:
 * недельный и двухнедельный доход тоже дают даты, а «когда как» не даёт их
 * вовсе — и тогда ответ `null`, а не выдуманная дата.
 */
export function nextIncomeDate(streams: IncomeStream[], today: PlainDate): PlainDate | null {
  const dates = streams
    .map((s) => nextForStream(s, today))
    .filter((d): d is PlainDate => d !== null);
  if (dates.length === 0) return null;
  return dates.reduce((a, b) => (a <= b ? a : b));
}

/** Сумма обязательства для планирования: у переменных берём верхнюю границу. */
export function obligationAmount(o: Obligation): Money {
  return o.amountType === 'fixed' ? o.amount : (o.amountEstimate ?? o.amount);
}

/**
 * «Можно потратить» — главная функция продукта (PRD §2.1).
 *
 * Возвращает не только итог, но и **построчный разбор**: экран, на котором
 * пользователь может сложить строки и получить ту же цифру, — это и есть
 * механизм доверия. Непрозрачное число пользователь не примет.
 */
export function computeAvailable(input: AvailableInput): AvailableResult {
  const { today } = input;

  const liquidLines: DeductionLine[] = input.accounts
    .filter((a) => a.isLiquid && !a.archived && a.kind !== 'credit_card')
    .map((a) => ({ kind: 'obligation' as const, id: a.id, label: a.name, amount: a.balance }));
  const liquid = add(...liquidLines.map((l) => l.amount));

  /*
    Горизонт по лестнице: расписание → подтверждённое ожидание → оценка по
    истории → «не знаю».

    Прежде на месте «не знаю» стояло первое число следующего месяца. Это и
    есть выдуманный горизонт: человеку без расписания дохода приложение молча
    обещало месяц, а цифра в день считалась делением на дни до этой даты.
    Теперь неизвестность названа своим именем в `horizon.basis`, а окно
    расчёта закрывается концом месяца — вычитать платежи всё равно нужно, но
    датой это окно больше не притворяется.
  */
  const resolved = resolveHorizon({
    today,
    streams: input.incomeStreams,
    ...(input.expectations ? { expectations: input.expectations } : {}),
    ...(input.incomeFacts ? { facts: input.incomeFacts } : {}),
    ...(input.trackingStartedAt ? { since: input.trackingStartedAt } : {}),
  });
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  /*
    Окно расчёта закрывает только **твёрдая** дата.

    Оценка по истории и неподтверждённый приход датой окна не становятся:
    первая — догадка, второй вовсе в прошлом, и деление на «дни до него» дало
    бы бессмыслицу. В обоих случаях окно закрывается концом месяца, а
    интерфейс, глядя на `horizon.basis`, говорит не «до такого-то», а
    «хватит на столько-то».
  */
  const horizon = confirmedDate(resolved) ?? ymd(year, month, daysInMonth(year, month));
  const daysToHorizon = Math.max(1, diffDays(today, horizon));

  const deductions: DeductionLine[] = [];
  const reserves = input.reserves ?? [];
  const liquidIds = new Set(liquidLines.map((l) => l.id));
  /*
    Покрытие требований считается по конвертам, которые правда держат деньги:
    конверт на счёте «не для трат» больше остатка этого счёта платёж не
    обеспечивает. Вычитаются при этом конверты как записаны — см.
    `backedReserves`.
  */
  const backed = backedReserves({
    accounts: input.accounts, reserves, obligations: input.obligations, today,
  });

  /** Сколько закреплено под конкретное требование, независимо от счёта. */
  const covered = (kind: ReserveTarget, id: string): Money =>
    add(...backed.filter((r) => r.forKind === kind && r.forId === id).map((r) => r.amount));

  // 0. Конверты на ликвидных счетах: эти деньги закреплены и свободными
  //    не считаются. Конверты на счетах «не для трат» вычитать не нужно —
  //    их там и так нет в ликвидных.
  for (const r of reserves) {
    if (!liquidIds.has(r.accountId)) continue;
    const account = input.accounts.find((a) => a.id === r.accountId);
    deductions.push({
      kind: 'reserve',
      id: r.id,
      label: `Закреплено на «${account?.name ?? 'счёте'}»`,
      amount: r.amount,
      note: reserveNote(r, input),
    });
  }

  // 1. Обязательства текущего цикла, которые ещё не подтверждены.
  //
  //    Цикл — окно между зарплатами, а не календарный месяц: платёж 5-го числа,
  //    когда сегодня 9-е, относится к этому циклу и, пока не подтверждён,
  //    держит резерв. «Просрочено» и «уже оплачено» для главной цифры означают
  //    противоположное, и различить их может только подтверждение.
  const cycleStart = cycleStartDate(input, today);
  const upcoming: UpcomingPayment[] = [];
  // Наступления до начала учёта человек платил мимо приложения: новый
  // человек с кредитом 20-го получал «срок был 20 октября, не хватает»,
  // хотя завёл кредит 3 ноября (03.11.2026).
  const watched = input.trackingStartedAt && isAfter(input.trackingStartedAt, cycleStart)
    ? input.trackingStartedAt : cycleStart;

  // Наступления берутся из общего календаря (§7 плана миграции): за цикл
  // может наступить несколько недельных платежей и ни одного годового.
  // Прежний расчёт брал ровно одну дату на обязательство и терял остальные.
  /*
    Наступления цикла плюс всё неурегулированное прошлое.

    Окно цикла отвечает за будущее до горизонта, а за прошлое отвечает единый
    селектор: платёж августа, который человек не отметил, обязан держать
    деньги и в сентябре. Раньше здесь было своё окно «от начала цикла», и
    просрочка старше цикла тихо исчезала из защищённого слоя.
  */
  const cycleInstances = mergeInstances(
    paymentInstances({
      obligations: input.obligations,
      reserves: backed,
      today,
      from: watched,
      to: horizon,
    }),
    unsettledInstances({
      obligations: input.obligations,
      reserves: backed,
      today,
      ...(input.trackingStartedAt ? { trackingStartedAt: input.trackingStartedAt } : {}),
    }),
  );

  for (const inst of cycleInstances) {
    const o = input.obligations.find((x) => x.id === inst.obligationId)!;
    const due = inst.scheduledDate;

    upcoming.push({
      kind: 'obligation', id: o.id, label: o.name, date: due,
      amount: inst.amount, covered: inst.secured,
      remaining: inst.remaining,
      secured: inst.remaining === ZERO,
      settled: inst.settled,
      due: inst.due,
      // Экземпляры выбраны внутри цикла: за горизонт выборка не выходит.
      afterHorizon: false,
    });

    if (inst.settled) continue;
    // В подписи показываем требование целиком: человек сверяет его со счётом.
    const full = inst.amount;
    const cov = inst.secured;
    const remaining = inst.remaining;
    if (remaining === ZERO && cov > ZERO) continue; // покрыто резервом целиком
    deductions.push({
      kind: 'obligation',
      id: o.id,
      // Ключ наступления: за цикл их может быть несколько, и различать строки
      // нужно по нему, а не по обязательству.
      instanceId: inst.id,
      label: o.name,
      amount: remaining,
      date: due,
      ...(!isAfter(due, today) && cov === ZERO
        ? { note: 'платёж уже наступил — подтверди или отметь неоплаченным' }
        : {}),
      ...(cov > ZERO
        ? { note: `из ${formatMoney(full)} закреплено ${formatMoney(cov)}` }
        : o.amountType === 'variable'
          ? { note: 'оценка по прошлым платежам' }
          : {}),
    });
  }

  // 2. Кредитки: вычитаем НЕДОБОР резерва, а не весь платёж.
  const termsById = new Map(input.creditTerms.map((t) => [t.accountId, t]));
  const credit: CreditState[] = [];
  /** Сколько уже вычтено под долг по каждой карте — ключ: счёт карты. */
  const creditDeducted = new Map<string, Money>();
  /*
    Платежи по картам — из прогноза, как и на остальных экранах: остаток
    выписки и покупки после неё. Второй платёж наступает через месяц после
    первого и до прихода обычно не доходит; но если доходит — например,
    приход далеко, — он требует сегодняшних денег так же, как первый.
  */
  const cardEvents = cardPayments({
    today, accounts: input.accounts, creditTerms: input.creditTerms, reserves: backed,
  });
  for (const a of input.accounts) {
    if (a.kind !== 'credit_card' || a.archived) continue;
    const t = termsById.get(a.id);
    if (!t) continue;
    const st = creditState(a, t, today);
    // Конверт под платёж уменьшает требование так же, как уже внесённый платёж,
    // но деньги при этом ещё на счёте — их вычел шаг 0.
    const cov = covered('credit', a.id);
    const missing = clampPos(sub(st.missing, cov));
    // `graceSecured` — деньги собраны (внесённое плюс конверт).
    // `settled` приходит из `creditState` и означает только фактический взнос.
    credit.push({ ...st, missing, graceSecured: missing === ZERO });
    // Платёж после ближайшего поступления оплачивается будущим доходом, а не
    // сегодняшним остатком. Раньше здесь проверки горизонта не было — в
    // отличие от обязательств и броней, — и весь долг по выписке вычитался
    // сразу. На настоящих данных это давало минус в главной цифре: платёж
    // 9 октября требовался с остатка 13 сентября, хотя зарплата 7 октября.
    const inCycle = !isAfter(st.paymentDate, horizon);
    if (st.needForGrace > ZERO) {
      upcoming.push({
        kind: 'credit', id: a.id, label: a.name, date: st.paymentDate,
        amount: st.needForGrace,
        covered: add(st.reserved, cov),
        remaining: missing,
        secured: missing === ZERO,
        // Оплата — это взнос на карту, а не собранный конверт. Раньше здесь
        // стояло `missing === ZERO`, и полный конверт читался как «оплачен».
        settled: st.settled,
        // Наступивший срок снимается только оплатой: большой остаток и
        // готовые деньги задачу «выполнить платёж» не закрывают.
        due: !isAfter(st.paymentDate, today) && !st.settled,
        afterHorizon: !inCycle,
      });
    }
    if (missing > ZERO && inCycle) {
      creditDeducted.set(a.id, missing);
      deductions.push({
        kind: 'credit',
        id: a.id,
        label: a.name,
        amount: missing,
        date: st.paymentDate,
        note: cov > ZERO
          ? `закреплено ${formatMoney(cov)}, дозарезервировать до ${dayWords(st.paymentDate, today)}`
          : `дозарезервировать до ${dayWords(st.paymentDate, today)}`,
      });
    }

    // Покупки после выписки — только если их платёж наступает до прихода.
    for (const e of cardEvents) {
      if (e.ref !== a.id || e.card?.part !== 'after_statement') continue;
      if (isAfter(e.date, horizon)) continue;
      const left = notCovered(e);
      upcoming.push({
        kind: 'credit', id: a.id, label: e.label, date: e.date,
        amount: e.amount, covered: e.covered, remaining: left,
        secured: left === ZERO,
        // Взнос на карту уменьшает долг, и покупки после выписки из прогноза
        // уходят сами: оплаченного платежа здесь не бывает.
        settled: false,
        due: !isAfter(e.date, today),
        afterHorizon: false,
      });
      if (left <= ZERO) continue;
      creditDeducted.set(a.id, add(creditDeducted.get(a.id) ?? ZERO, left));
      deductions.push({
        kind: 'credit',
        id: a.id,
        label: e.label,
        amount: left,
        date: e.date,
        note: `покупки после выписки, внести до ${dayWords(e.date, today)}`,
      });
    }
  }

  // 3. Цели: взносы, ещё не сделанные в текущем периоде.
  const loansById = new Map((input.loans ?? []).map((l) => [l.id, l]));
  for (const g of input.goals) {
    // Взнос за этот приход сделан — резерв больше не держим: деньги уже
    // отложены руками, и вычитать их второй раз значило бы наказать за то,
    // что человек сделал всё правильно.
    if (g.status !== 'active' || isFunded(g, cycleStart)) continue;
    // Цель, которая ещё не началась, денег не просит: копить начнём с дохода,
    // а не с остатка недожитого цикла.
    if (g.startDate && isAfter(g.startDate, today)) continue;
    if (g.contributionMode === 'residual') continue;
    if (g.monthlyContribution <= ZERO) continue;
    const cov = covered('goal', g.id);
    // Цель «закрыть долг» по карте не вычитает то, что уже вычтено под
    // беспроцентный период: это одни и те же деньги и один и тот же долг.
    // Взнос считается только сверх обязательного — он и есть досрочное
    // гашение.
    const loan = g.loanId ? loansById.get(g.loanId) : undefined;
    const already = loan?.card ? creditDeducted.get(loan.card.accountId) ?? ZERO : ZERO;
    const remaining = clampPos(sub(sub(g.monthlyContribution, cov), already));
    if (remaining === ZERO) continue;
    deductions.push({
      kind: 'goal', id: g.id, label: g.name,
      amount: remaining,
      note: already > ZERO
        ? `сверх платежа по карте — он вычтен отдельно`
        : cov > ZERO ? `взнос месяца, закреплено ${formatMoney(cov)}` : 'взнос месяца',
    });
  }

  // 4. Брони на планируемые траты до горизонта.
  for (const p of input.plannedExpenses) {
    if (p.status !== 'active') continue;
    if (isAfter(p.expectedDate, horizon)) continue;
    const cov = covered('planned', p.id);
    const remaining = clampPos(sub(p.amount, cov));
    if (remaining === ZERO) continue;
    deductions.push({
      kind: 'planned', id: p.id, label: p.name,
      amount: remaining, date: p.expectedDate,
      note: cov > ZERO ? `запланировано, закреплено ${formatMoney(cov)}` : 'запланировано',
    });
  }

  // Порядок вычетов детерминирован: сначала по виду, внутри вида — по дате.
  // Блок называется «Ближайшие платежи», и порядок создания в базе для него
  // не значит ничего — первым обязан стоять тот, что наступает раньше.
  sortDeductions(deductions);

  const groups: Record<DeductionKind, Money> = {
    reserve: sumOf(deductions, 'reserve'),
    obligation: sumOf(deductions, 'obligation'),
    credit: sumOf(deductions, 'credit'),
    goal: sumOf(deductions, 'goal'),
    planned: sumOf(deductions, 'planned'),
  };

  const available = sub(liquid, add(...deductions.map((d) => d.amount)));
  const perDay = available > ZERO ? perUnitFloor(available, daysToHorizon, 100) : ZERO;

  const reservedOutsideLiquid = add(
    ...reserves.filter((r) => !liquidIds.has(r.accountId)).map((r) => r.amount),
  );

  upcoming.sort((a, b) => (a.date === b.date ? a.label.localeCompare(b.label, 'ru') : a.date < b.date ? -1 : 1));

  return {
    liquid, liquidLines, deductions, groups, available,
    horizonDate: horizon, horizon: resolved,
    daysToHorizon, perDay, credit, upcoming, reservedOutsideLiquid,
  };
}

const sumOf = (lines: DeductionLine[], kind: DeductionKind): Money =>
  add(...lines.filter((l) => l.kind === kind).map((l) => l.amount));

/**
 * Разбор для экрана «Как считается» — плоский список строк в том порядке,
 * в котором они показываются пользователю, с промежуточными итогами.
 */
export interface BreakdownRow {
  label: string;
  amount: Money;
  level: 'item' | 'subtotal' | 'total';
  note?: string;
}

export function breakdownRows(r: AvailableResult): BreakdownRow[] {
  const rows: BreakdownRow[] = [];
  for (const l of r.liquidLines) rows.push({ label: l.label, amount: l.amount, level: 'item' });
  rows.push({ label: 'Баланс ликвидных счетов', amount: r.liquid, level: 'subtotal' });

  const push = (kind: DeductionKind, title: string) => {
    const lines = r.deductions.filter((d) => d.kind === kind);
    if (lines.length === 0) return;
    for (const l of lines) {
      rows.push({ label: l.label, amount: kop(-l.amount), level: 'item', ...(l.note ? { note: l.note } : {}) });
    }
    rows.push({ label: title, amount: kop(-r.groups[kind]), level: 'subtotal' });
  };
  push('reserve', 'Минус закреплено на счетах');
  push('obligation', 'Минус обязательные платежи');
  push('credit', 'Минус резерв кредиток');
  push('goal', 'Минус цели (резерв)');
  push('planned', 'Минус запланировано');

  rows.push({ label: 'Можно потратить', amount: r.available, level: 'total' });
  return rows;
}

/** Человекочитаемая проверка: строки складываются в итог. */
export function assertBreakdownBalances(r: AvailableResult): void {
  const sum = sub(r.liquid, add(...r.deductions.map((d) => d.amount)));
  if (sum !== r.available) {
    throw new Error(
      `Разбор не сходится: ${formatMoney(sum)} против ${formatMoney(r.available)}`,
    );
  }
}

/** Под что закреплён резерв — человеческим языком для строки разбора. */
function reserveNote(r: Reserve, input: AvailableInput): string {
  switch (r.forKind) {
    case 'credit': {
      const acc = input.accounts.find((a) => a.id === r.forId);
      return `под платёж по «${acc?.name ?? 'кредитке'}»`;
    }
    case 'obligation': {
      const o = input.obligations.find((x) => x.id === r.forId);
      return `под «${o?.name ?? 'платёж'}»`;
    }
    case 'goal': {
      const g = input.goals.find((x) => x.id === r.forId);
      return `под цель «${g?.name ?? '—'}»`;
    }
    case 'planned': {
      const p = input.plannedExpenses.find((x) => x.id === r.forId);
      return `под «${p?.name ?? 'покупку'}»`;
    }
    // Буфер стабильного бюджета: деньги, из которых человек платит себе.
    // Названия плана здесь нет намеренно — план один, и «буфер» человек
    // узнаёт быстрее, чем любое придуманное имя.
    case 'stable':
      return 'в буфере стабильного бюджета';
  }
}

/**
 * Начало текущего цикла — предыдущая зарплата.
 *
 * Без заданного дохода цикла нет, и берём месяц назад: это грубо, но иначе
 * обязательства, наступившие до сегодня, выпали бы из расчёта совсем.
 */
function cycleStartDate(input: AvailableInput, today: PlainDate): PlainDate {
  // Сначала факт, потом расписание — общее правило живёт в `income.ts`.
  // Без цикла вовсе берём месяц назад: иначе платежи, наступившие до
  // сегодня, выпали бы из расчёта совсем.
  return cycleStartFrom(input.incomeStreams, input.incomeFacts ?? [], today)
    ?? addDays(today, -31);
}

/**
 * Дата платежа этого цикла: между началом цикла и горизонтом.
 * Платёж, наступающий сегодня, тоже считается — до конца дня он не оплачен,
 * и снимать резерв утром нельзя.
 */
function dueInCycle(
  dayOfMonth: number,
  rule: import('./date.js').ShiftRule,
  cycleStart: PlainDate,
  horizon: PlainDate,
  today: PlainDate,
): PlainDate | null {
  const upcoming = nextOccurrenceInclusive(today, dayOfMonth, rule);
  if (!isAfter(upcoming, horizon)) return upcoming;
  const past = prevOccurrenceInclusive(today, dayOfMonth, rule);
  if (isBefore(past, cycleStart)) return null;
  return past;
}

const KIND_ORDER: Record<DeductionKind, number> = {
  reserve: 0, obligation: 1, credit: 2, goal: 3, planned: 4,
};

/** Стабильный порядок: вид, затем дата, затем название. */
function sortDeductions(lines: DeductionLine[]): void {
  lines.sort((a, b) => {
    const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
    if (byKind !== 0) return byKind;
    if (a.date && b.date && a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.date && !b.date) return -1;
    if (!a.date && b.date) return 1;
    return a.label.localeCompare(b.label, 'ru');
  });
}

/**
 * Склейка двух наборов наступлений по ключу.
 *
 * Окно цикла и неурегулированное прошлое пересекаются: платёж этого месяца
 * попадает в оба. Показать его дважды значит вычесть деньги дважды.
 */
function mergeInstances(
  cycle: readonly PaymentInstance[], overdue: readonly PaymentInstance[],
): PaymentInstance[] {
  const byId = new Map<string, PaymentInstance>();
  for (const inst of [...overdue, ...cycle]) byId.set(inst.id, inst);
  return [...byId.values()].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
}
